import React from 'react';
import { Button, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import {
  getTransportStatusDisplay,
  getTransportStatusDisplayTranslationKey,
  TRANSPORT_STATUS_DISPLAY_COLORS,
} from '../transportRouteViews';
import type { SupplierOrderRealizationRow } from '../supplierOrderRealization';
import './TransportOrderRealizationCard.css';

const { Text } = Typography;

interface Props {
  row: SupplierOrderRealizationRow;
  transportStatus: string;
  onOpenSupplierOrder: () => void;
}

/**
 * INBOUND truck: one ZD's realization — Plan / Przyjęto / Nie przyjęto per line
 * from this truck's received PZ (supplierOrderRealization.ts). Same layout and
 * styling as the ZK `TransportOrderRealizationCard`. Non-financial.
 */
const SupplierOrderRealizationCard: React.FC<Props> = ({ row, transportStatus, onOpenSupplierOrder }) => {
  const { t, i18n } = useTranslation(['transport']);
  const displayStatus = getTransportStatusDisplay(transportStatus);
  const quantity = (value: number | null, unit: string | null) => (
    value === null || unit === null
      ? t('transport:supplierOrders.freight.unknown')
      : `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit, i18n.language)}`
  );

  return (
    <article className={`transport-order-realization-card is-${displayStatus}`} data-testid="transport-supplier-realization-card">
      <header className="transport-order-realization-card__header">
        <div className="transport-order-realization-card__identity">
          <Button type="link" className="transport-order-realization-card__order-link" onClick={onOpenSupplierOrder}>
            {row.label}
          </Button>
          <Text type="secondary"> · {row.supplierName || '—'}</Text>
        </div>
        <div className="transport-order-realization-card__actions">
          <Tag color={TRANSPORT_STATUS_DISPLAY_COLORS[displayStatus]} bordered={false}>
            {t(getTransportStatusDisplayTranslationKey(transportStatus))}
          </Tag>
        </div>
      </header>
      <div className="transport-order-realization-card__cargo-label">{t('transport:supplierOrders.realization.cargoLabel')}</div>
      <div className="transport-order-realization-card__cargo">
        {row.lines.length ? row.lines.map((line) => (
          <div className={`transport-order-realization-card__cargo-row is-${line.state}`} key={line.id} data-testid="transport-supplier-realization-line">
            <div className="transport-order-realization-card__cargo-product">
              <strong>{line.name}</strong>
              {line.dimensions ? <Text type="secondary" className="transport-order-realization-card__loading">{line.dimensions}</Text> : null}
            </div>
            <div className="transport-order-realization-card__cargo-quantities">
              <span><small>{t('transport:deliveryConfirmation.planLabel')}</small><b>{quantity(line.plan, line.unit)}</b></span>
              <span className="transport-order-realization-card__delivered"><small>{t('transport:supplierOrders.realization.received')}</small><b>{quantity(line.received, line.unit)}</b></span>
              <span className="transport-order-realization-card__not-delivered"><small>{t('transport:supplierOrders.realization.notReceived')}</small><b>{quantity(line.notReceived, line.unit)}</b></span>
            </div>
            {line.receivedPercent !== null ? (
              <span className="transport-order-realization-card__cargo-progress" aria-label={`${formatLocalizedQuantity(line.receivedPercent, i18n.language)}%`}>
                <i style={{ width: `${line.receivedPercent}%` }} />
              </span>
            ) : null}
          </div>
        )) : <Text type="secondary">{t('transport:supplierOrders.realization.noLines')}</Text>}
      </div>
      <div className="transport-order-realization-card__metrics" data-testid="transport-supplier-realization-documents">
        {row.receiptDocuments.length > 0 ? (
          <span>{t('transport:supplierOrders.realization.receiptDocuments')}: {row.receiptDocuments.map((document) => document.pzNumber).join(', ')}</span>
        ) : (
          <span>{t('transport:supplierOrders.realization.noReceipt')}</span>
        )}
      </div>
      {row.hasSharedReceiptDocument ? (
        <div className="transport-order-realization-card__shared" data-testid="transport-supplier-realization-shared">
          <Text type="secondary">{t('transport:supplierOrders.realization.sharedDocument')}</Text>
        </div>
      ) : null}
    </article>
  );
};

export default SupplierOrderRealizationCard;
